/**
 * CLINICAL MODULES — the one list the API, the web app and Platform Admin share.
 *
 * Kris, 2026-10-01 (clinical safety case CS-02, HM-01). JeanieIQ's intended
 * purpose for pharmacy is to highlight NHS service opportunities and keep
 * service and statutory records: no decisions about patients, no handling of
 * medicines. The modules below go further, so each is HIDDEN until the
 * Clinical Safety Officer signs it into the safety case, and then switched on
 * PER MODULE, PER TENANT by a JeanieIQ platform admin.
 *
 * Per module because the safety case grows one module at a time: clearing
 * Pharmacy First must not expose the anticoagulation clinic too.
 *
 * Stored on the tenant as settings.clinicalModules: string[] of these keys.
 * Anything that is not an array of known keys counts as nothing switched on
 * (fail closed), so a malformed or hand-edited setting hides, never exposes.
 *
 * `api` are route prefixes (exact, `prefix/…` or `prefix?…`); `apiPatterns`
 * are regex sources for a single route inside a router that otherwise stays
 * live; `web` are the page routes the hub and sidebar stop offering.
 */
export interface ClinicalModule {
  key: string;
  label: string;
  api: readonly string[];
  apiPatterns?: readonly string[];
  web: readonly string[];
}

export const CLINICAL_MODULES = [
  { key: 'pharmacy_first', label: 'Pharmacy First (consultations and triage)', api: ['/api/pharmacy-first', '/api/pf-triage'], web: ['/pharmacy-first', '/pf-triage'] },
  { key: 'pcs', label: 'Pharmacy Contraception Service', api: ['/api/pcs'], web: ['/pcs'] },
  { key: 'hypertension', label: 'Hypertension case-finding', api: ['/api/hypertension'], web: ['/hypertension'] },
  { key: 'anticoag', label: 'Anticoagulation monitoring', api: ['/api/anticoag'], web: ['/anticoag'] },
  { key: 'emergency_supply', label: 'Emergency supply', api: ['/api/ems'], web: ['/ems'] },
  { key: 'supervised', label: 'Supervised consumption', api: ['/api/supervised'], web: ['/supervised'] },
  {
    key: 'care_homes', label: 'Care homes (residents, cycles, audits, portal)',
    api: ['/api/care-homes', '/api/care-home-cycles'],
    web: ['/care-homes', '/care-home-cycles', '/care-home-receive', '/delivery-wall-chart', '/care-home-audits'],
  },
  { key: 'blister_packs', label: 'Blister packs and DAA/MCA assessments', api: ['/api/blister-specials', '/api/daa'], web: ['/blister-specials', '/daa'] },
  { key: 'carers', label: 'Carer authorisations', api: ['/api/carers'], web: ['/carers'] },
  { key: 'script_watch', label: 'Script watch', api: ['/api/script-watch'], web: ['/script-watch'] },
  { key: 'pmr_safety_watch', label: 'PMR Safety Watch (record checks)', api: [], apiPatterns: ['^/api/pmr/patients/[^/]+/check/?$'], web: [] },
  { key: 'patient_comms', label: 'Patient communications log', api: ['/api/patient-comms'], web: [] },
] as const satisfies readonly ClinicalModule[];

export type ClinicalModuleKey = (typeof CLINICAL_MODULES)[number]['key'];

const KNOWN = new Set<string>(CLINICAL_MODULES.map((m) => m.key));

/** True if `key` is a clinical module key. */
export function isClinicalModuleKey(key: string): key is ClinicalModuleKey {
  return KNOWN.has(key);
}

/**
 * The clinical modules a tenant has switched on, read fail-closed from its
 * settings JSON: only settings.clinicalModules as an array, and only the known
 * keys within it. Anything else is an empty set.
 */
export function enabledClinicalModules(settings: unknown): Set<ClinicalModuleKey> {
  const out = new Set<ClinicalModuleKey>();
  if (typeof settings !== 'object' || settings === null) return out;
  const raw = (settings as Record<string, unknown>)['clinicalModules'];
  if (!Array.isArray(raw)) return out;
  for (const k of raw) if (typeof k === 'string' && isClinicalModuleKey(k)) out.add(k);
  return out;
}

/** The clinical module a web page route belongs to, or null if it is not clinical. */
export function clinicalModuleForWebRoute(route: string): ClinicalModuleKey | null {
  for (const m of CLINICAL_MODULES) if ((m.web as readonly string[]).includes(route)) return m.key;
  return null;
}
